import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, ViewChild, TemplateRef, ChangeDetectorRef } from '@angular/core';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { FeedbackService } from 'src/app/core/services/feedback.service';
import { MatDialog } from '@angular/material/dialog';
import { BillingService } from 'src/app/core/services/billing.service';
import { Router } from '@angular/router';
import { takeUntil, forkJoin } from 'rxjs';
import { PatientService } from 'src/app/core/services/patient.service';
import { GroupService } from 'src/app/core/services/group.service';
import { ThemeService } from 'src/app/core/services/theme.service';
import { NgxMaterialTimepickerTheme } from 'ngx-material-timepicker';
import { ProcedureCode } from 'src/app/core/services/app-data.service';
import { PatientBillingService } from 'src/app/core/services/patient-billing.service';
import { ChangeTrackerService } from './change-tracker.service';
import { PendingChange } from './change-tracker.interface';
import * as moment from 'moment';
import * as _ from 'lodash';

@Component({
    selector: 'app-inline-master-billing-editor',
    templateUrl: './inline-master-billing-editor.component.html',
    styleUrls: ['./inline-master-billing-editor.component.scss'],
    providers: [ChangeTrackerService]
})
export class InlineMasterBillingEditorComponent extends BaseComponent implements OnInit, OnChanges {
    @Input() filterValue: string = '';
    @Input() statusFilters: string = '';
    @Output() dataChange = new EventEmitter<any[]>();

    allData: any[] = [];
    filteredData: any[] = [];
    clonedData: any[] = [];
    groupedPatients: any[] = [];
    expandedPatients: Set<string> = new Set();
    expandedDates: Set<string> = new Set();
    expandedTimes: Set<string> = new Set();

    // Code creation properties
    creatingCode: any = {};
    isCreatingCode: boolean = false;
    selectedProcedureCodes: ProcedureCode[] = [];
    groupUsers: any[] = [];
    darkTheme!: NgxMaterialTimepickerTheme;

    // Time editing properties
    editingTime: any = {};
    isEditingTime: boolean = false;

    // Provider editing properties
    editingProvider: any = {};
    isEditingProvider: boolean = false;

    // Individual code editing properties
    editingCode: any = {};
    selectedProviderId: string | null = null;

    // Loading state for data persistence operations
    isPersistingData: boolean = false;

    // Change tracking properties
    hasPendingChanges: boolean = false;

    @ViewChild('addCodeDialog') addCodeDialog?: TemplateRef<any>;
    @ViewChild('editTimeDialog') editTimeDialog?: TemplateRef<any>;
    @ViewChild('editProviderDialog') editProviderDialog?: TemplateRef<any>;

    constructor(
        private billingService: BillingService,
        private patientService: PatientService,
        private groupService: GroupService,
        private themeService: ThemeService,
        private patientBillingService: PatientBillingService,
        private changeTracker: ChangeTrackerService,
        private router: Router,
        private changeDetectorRef: ChangeDetectorRef,
        fs: FeedbackService,
        md: MatDialog
    ) {
        super(fs, md);
    }

    ngOnInit(): void {
        this.loadData();
        this.loadGroupUsers();
        this.loadTheme();
        this.setupChangeTracking();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['filterValue'] || changes['statusFilters']) {
            this.applyFilter();
        }
    }

    override ngOnDestroy(): void {
        // Clear pending changes
        this.changeTracker.clearChanges();

        super.ngOnDestroy();
    }

    private loadData(): void {
        this.billingService.groupBilling$
            .pipe(takeUntil(this.destroy$))
            .subscribe((results: any) => {
                this.allData = results;
                this.applyFilter();
            });
    }

    private loadGroupUsers(): void {
        // Subscribe to group users for provider selection (same as patient-coding-detail)
        this.groupService.groupUsers$
            .pipe(takeUntil(this.destroy$))
            .subscribe((users: any[]) => {
                if (users && users.length > 0) {
                    // Filter to only show doctors (same as patient-coding-detail)
                    this.groupUsers = users.filter(u => u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50') || [];
                    //console.log('Loaded group users for providers:', this.groupUsers);
                } else {
                    console.log('No group users available');
                    this.groupUsers = [];
                }
            });
    }

    private loadTheme(): void {
        this.themeService.isDarkMode$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isDarkMode) => {
                this.darkTheme = this.themeService.getTimepickerTheme(isDarkMode);
            });
    }

    private setupChangeTracking(): void {
        this.changeTracker.pendingChanges$
            .pipe(takeUntil(this.destroy$))
            .subscribe((changes) => {
                this.hasPendingChanges = changes.size > 0;
                /* console.log('Change tracking updated:', {
                    changesCount: changes.size,
                    hasPendingChanges: this.hasPendingChanges,
                    changes: Array.from(changes.values()).map(c => ({ type: c.type, description: c.description }))
                }); */
            });
    }

    private applyFilter(): void {
        let filtered = this.allData;

        // Apply text filter
        if (this.filterValue.trim() !== '') {
            const filterTerms = this.filterValue.toLowerCase().split(' ');
            filtered = filtered.filter(item => {
                const icdCodesMatch = item.Patient.icdCodes?.some((icdCode: any) =>
                    filterTerms.every(term =>
                        (icdCode.code || '').toLowerCase().includes(term) ||
                        (icdCode.description || '').toLowerCase().includes(term)
                    )
                );

                const patientMatch = filterTerms.every(term =>
                    item.Patient.name.toLowerCase().includes(term) ||
                    item.Patient.surname.toLowerCase().includes(term) ||
                    item.Patient.patientNumber?.toLowerCase().includes(term) ||
                    item.Patient.diagnosis?.toLowerCase().includes(term) ||
                    item.code?.toLowerCase().includes(term)
                );

                return icdCodesMatch || patientMatch;
            });
        }

        // Apply status filter
        if (this.statusFilters === 'not_submitted') {
            filtered = filtered.filter(item => !item.connectProcedureCodeId);
        } else if (this.statusFilters === 'submitted') {
            filtered = filtered.filter(item => !!item.connectProcedureCodeId);
        } else if (this.statusFilters) {
            filtered = filtered.filter(item => item.Patient.status === this.statusFilters);
        }

        this.filteredData = filtered;
        this.groupByPatient();
        this.cloneData();
    }

    private groupByPatient(): void {
        const grouped = new Map<string, any>();

        this.filteredData.forEach(item => {
            const patientId = item.Patient.id;
            if (!grouped.has(patientId)) {
                grouped.set(patientId, {
                    patient: item.Patient,
                    dateGroups: new Map(),
                    totalRecords: 0
                });
            }

            // Use consistent date key generation without timezone issues
            const dateKey = item.date ? this.generateDateKey(item.date) : 'Unknown Date';
            if (!grouped.get(patientId).dateGroups.has(dateKey)) {
                grouped.get(patientId).dateGroups.set(dateKey, {
                    date: item.date,
                    dateKey: dateKey,
                    timeGroups: new Map(),
                    count: 0
                });
            }

            // Group by time if available, otherwise use "No Time" as fallback
            const timeKey = this.generateTimeKey(item.startTime, item.endTime);

            if (!grouped.get(patientId).dateGroups.get(dateKey).timeGroups.has(timeKey)) {
                grouped.get(patientId).dateGroups.get(dateKey).timeGroups.set(timeKey, {
                    startTime: item.startTime,
                    endTime: item.endTime,
                    timeKey: timeKey,
                    providerGroups: new Map(),
                    count: 0
                });
            }

            // Group by provider if available, otherwise use "No Provider" as fallback
            const providerKey = item.providerId || 'No Provider';
            const providerName = item.provider ? `${item.provider.title} ${item.provider.name} ${item.provider.surname}`.trim() : 'No Provider';

            if (!grouped.get(patientId).dateGroups.get(dateKey).timeGroups.get(timeKey).providerGroups.has(providerKey)) {
                grouped.get(patientId).dateGroups.get(dateKey).timeGroups.get(timeKey).providerGroups.set(providerKey, {
                    providerId: item.providerId,
                    providerName: providerName,
                    records: [],
                    count: 0
                });
            }

            grouped.get(patientId).dateGroups.get(dateKey).timeGroups.get(timeKey).providerGroups.get(providerKey).records.push(item);
            grouped.get(patientId).dateGroups.get(dateKey).timeGroups.get(timeKey).providerGroups.get(providerKey).count++;
            grouped.get(patientId).dateGroups.get(dateKey).timeGroups.get(timeKey).count++;
            grouped.get(patientId).dateGroups.get(dateKey).count++;
            grouped.get(patientId).totalRecords++;
        });

        // Convert Maps to arrays for template binding and sort them
        this.groupedPatients = Array.from(grouped.values())
            .map(patient => ({
                ...patient,
                dateGroups: Array.from(patient.dateGroups.values())
                    .sort((a: any, b: any) => {
                        // Sort dates chronologically (newest first) using moment
                        const dateA = moment(a.date);
                        const dateB = moment(b.date);
                        return dateB.valueOf() - dateA.valueOf();
                    })
                    .map((dateGroup: any) => ({
                        ...dateGroup,
                        timeGroups: Array.from(dateGroup.timeGroups.values())
                            .sort((a: any, b: any) => {
                                // Sort time groups chronologically by start time (latest first)
                                const timeA = a.startTime || '00:00';
                                const timeB = b.startTime || '00:00';
                                return timeB.localeCompare(timeA);
                            })
                            .map((timeGroup: any) => {
                                // Convert provider groups to arrays
                                const providerGroupsArray = Array.from(timeGroup.providerGroups.values()).map((providerGroup: any) => {
                                    return {
                                        ...providerGroup,
                                        providerGroupKey: `${patient.patient.id}-${dateGroup.dateKey}-${timeGroup.timeKey}-${providerGroup.providerId || 'no-provider'}`
                                    };
                                });

                                return {
                                    ...timeGroup,
                                    providerGroups: providerGroupsArray.filter((pg: any) => pg.count > 0)
                                };
                            })
                    }))
            }))
            .sort((a: any, b: any) => {
                // Sort patients alphabetically by name (case insensitive)
                const nameA = `${a.patient.name} ${a.patient.surname}`.toLowerCase();
                const nameB = `${b.patient.name} ${b.patient.surname}`.toLowerCase();
                return nameA.localeCompare(nameB);
            });
    }

    private cloneData(): void {
        this.clonedData = JSON.parse(JSON.stringify(this.filteredData));
    }

    toggleExpansion(type: 'patient' | 'date' | 'time', patientId: string, dateKey?: string, timeKey?: string): void {
        let key: string;
        let set: Set<string>;

        switch (type) {
            case 'patient':
                key = patientId;
                set = this.expandedPatients;
                break;
            case 'date':
                key = `date-${patientId}-${dateKey}`;
                set = this.expandedDates;
                break;
            case 'time':
                key = `time-${patientId}-${dateKey}-${timeKey}`;
                set = this.expandedTimes;
                break;
            default:
                return;
        }

        if (set.has(key)) {
            set.delete(key);
        } else {
            set.add(key);
        }
    }

    isExpanded(type: 'patient' | 'date' | 'time', patientId: string, dateKey?: string, timeKey?: string): boolean {
        let key: string;
        let set: Set<string>;

        switch (type) {
            case 'patient':
                key = patientId;
                set = this.expandedPatients;
                break;
            case 'date':
                key = `date-${patientId}-${dateKey}`;
                set = this.expandedDates;
                break;
            case 'time':
                key = `time-${patientId}-${dateKey}-${timeKey}`;
                set = this.expandedTimes;
                break;
            default:
                return false;
        }

        return set.has(key);
    }

    // Bulk expansion methods
    toggleAllSublevels(patientId: string): void {
        const patientGroup = this.groupedPatients.find(p => p.patient.id === patientId);
        if (!patientGroup) return;

        const isPatientExpanded = this.expandedPatients.has(patientId);

        if (isPatientExpanded) {
            // Collapse all sublevels
            this.expandedPatients.delete(patientId);
            this.expandedDates.clear();
            this.expandedTimes.clear();
        } else {
            // Expand all sublevels
            this.expandedPatients.add(patientId);

            // Expand all dates for this patient
            patientGroup.dateGroups.forEach((dateGroup: any) => {
                const dateKey = `date-${patientId}-${dateGroup.dateKey}`;
                this.expandedDates.add(dateKey);

                // Expand all times for this date
                dateGroup.timeGroups.forEach((timeGroup: any) => {
                    const timeKey = `time-${patientId}-${dateGroup.dateKey}-${timeGroup.timeKey}`;
                    this.expandedTimes.add(timeKey);
                });
            });
        }
    }

    toggleAllTimes(patientId: string, dateKey: string): void {
        const patientGroup = this.groupedPatients.find(p => p.patient.id === patientId);
        if (!patientGroup) return;

        const dateGroup = patientGroup.dateGroups.find((dg: any) => dg.dateKey === dateKey);
        if (!dateGroup) return;

        const dateExpansionKey = `date-${patientId}-${dateKey}`;
        const isDateExpanded = this.expandedDates.has(dateExpansionKey);

        if (isDateExpanded) {
            // Collapse the date and all times for this date
            this.expandedDates.delete(dateExpansionKey);
            dateGroup.timeGroups.forEach((timeGroup: any) => {
                const timeKey = `time-${patientId}-${dateKey}-${timeGroup.timeKey}`;
                this.expandedTimes.delete(timeKey);
            });
        } else {
            // Expand the date and all times for this date
            this.expandedDates.add(dateExpansionKey);
            dateGroup.timeGroups.forEach((timeGroup: any) => {
                const timeKey = `time-${patientId}-${dateKey}-${timeGroup.timeKey}`;
                this.expandedTimes.add(timeKey);
            });
        }
    }

    createCode(type: 'patient' | 'date' | 'time', patientId: string, date?: string, startTime?: string, endTime?: string): void {
        // Find the patient data
        //console.log('createCode: ' + date);
        const patient = this.groupedPatients.find(p => p.patient.id === patientId)?.patient;
        // Format date properly for the datepicker using moment (same as patient-coding-detail)
        let formattedDate: any;
        if (date) {
            // Use the same date handling as generateDateKey to avoid timezone issues
            //console.log('parsedDate: ' + date);
            // If it's already a moment object, use it directly
            if (moment.isMoment(date)) {
                formattedDate = date;
            } else {
                // Parse as a regular date and create moment in local timezone
                const parsedDate = new Date(date);
                formattedDate = moment(parsedDate);
            }
            if (!formattedDate.isValid()) {
                formattedDate = moment(); // fallback to today
            }
        } else {
            // Use today's date
            formattedDate = moment();
        }
        //console.log('formattedDate: ' + formattedDate.format('YYYY-MM-DD'));

        this.creatingCode = {
            patientId: patientId,
            patient: patient,
            date: formattedDate,
            startTime: startTime || '',
            endTime: endTime || '',
            code: '',
            description: '',
            paidCash: false,
            noCharge: false,
            providerId: null
        };

        // Set the dialog title based on type
        this.creatingCode.createType = type;

        this.isCreatingCode = true;
        this.dialog.open(this.addCodeDialog!);
    }

    private generateDateKey(date: string | Date | any): string {
        try {
            // Handle moment objects specially to avoid timezone issues
            if (moment.isMoment(date)) {
                // Use moment's format method to get the date in YYYY-MM-DD format
                return date.utc().format('YYYY-MM-DD');
            }

            // Parse the date and create a consistent key without timezone issues
            const parsedDate = new Date(date);
            if (isNaN(parsedDate.getTime())) {
                return 'Unknown Date';
            }
            // Use local date components to avoid timezone shifts
            const year = parsedDate.getFullYear();
            const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
            const day = String(parsedDate.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            return 'Unknown Date';
        }
    }

    private generateTimeKey(startTime?: string, endTime?: string): string {
        // Group by time if available, otherwise use "No Time" as fallback
        if (startTime && endTime) {
            return `${startTime}-${endTime}`;
        } else if (startTime) {
            return `${startTime}-No End Time`;
        } else if (endTime) {
            return `No Start Time-${endTime}`;
        } else {
            return 'No Time Specified';
        }
    }

    editTimeGroup(patientId: string, dateKey: string, timeKey: string, currentStartTime: string, currentEndTime: string): void {
        this.editingTime = {
            patientId: patientId,
            dateKey: dateKey,
            timeKey: timeKey,
            startTime: currentStartTime || '',
            endTime: currentEndTime || '',
            patientName: this.groupedPatients.find(p => p.patient.id === patientId)?.patient?.name || 'Unknown Patient'
        };

        this.isEditingTime = true;
        this.dialog.open(this.editTimeDialog!);
    }

    editProviderGroup(patientId: string, dateKey: string, timeKey: string, providerId: string, providerName: string): void {
        this.editingProvider = {
            patientId: patientId,
            dateKey: dateKey,
            timeKey: timeKey,
            providerId: providerId,
            providerName: providerName,
            newProviderId: providerId, // Initialize with current provider
            patientName: this.groupedPatients.find(p => p.patient.id === patientId)?.patient?.name || 'Unknown Patient'
        };

        this.isEditingProvider = true;
        this.dialog.open(this.editProviderDialog!);
    }

    saveTimeEdit(): void {
        if (!this.editingTime.startTime || !this.editingTime.endTime) {
            this.showToastMessage('Please enter both start and end times');
            return;
        }

        // Find the patient group
        const patientGroup = this.groupedPatients.find(p => p.patient.id === this.editingTime.patientId);
        if (!patientGroup) return;

        // Find the date group
        const dateGroup = patientGroup.dateGroups.find((dg: any) => dg.dateKey === this.editingTime.dateKey);
        if (!dateGroup) return;

        // Find the time group
        const timeGroup = dateGroup.timeGroups.find((tg: any) => tg.timeKey === this.editingTime.timeKey);
        if (!timeGroup) return;

        // Collect all the codes that need to be updated
        const codesToUpdate: any[] = [];
        timeGroup.providerGroups.forEach((providerGroup: any) => {
            providerGroup.records.forEach((record: any) => {
                if (record.id && !record.id.startsWith('temp_')) {
                    codesToUpdate.push({
                        id: record.id, // Include the ID for the service call
                        date: record.date, // Required field for backend
                        groupId: record.groupId,
                        code: record.code,
                        description: record.description,
                        startTime: this.editingTime.startTime,
                        endTime: this.editingTime.endTime,
                        weight: record.weight,
                        height: record.height,
                        BMI: record.BMI,
                        paidCash: record.paidCash,
                        noCharge: record.noCharge,
                        providerId: record.providerId,
                        displayIndex: record.displayIndex
                    });
                }
            });
        });

        if (codesToUpdate.length === 0) {
            this.showToastMessage('No codes found to update');
            return;
        }

        // Track the change instead of immediately persisting
        const changeId = this.changeTracker.generateChangeId('time_update');
        const pendingChange: PendingChange = {
            id: changeId,
            type: 'time-update',
            data: {
                patientId: this.editingTime.patientId,
                dateKey: this.editingTime.dateKey,
                timeKey: this.editingTime.timeKey,
                startTime: this.editingTime.startTime,
                endTime: this.editingTime.endTime,
                codesToUpdate: codesToUpdate,
                timeGroup: timeGroup
            },
            timestamp: Date.now(),
            description: `Update times for ${codesToUpdate.length} codes in time group ${this.editingTime.startTime}-${this.editingTime.endTime}`
        };

        this.changeTracker.addChange(pendingChange);

        // Update local data structure immediately for responsive UI
        timeGroup.providerGroups.forEach((providerGroup: any) => {
            providerGroup.records.forEach((record: any) => {
                record.startTime = this.editingTime.startTime;
                record.endTime = this.editingTime.endTime;
            });
        });

        // Update the time group properties
        timeGroup.startTime = this.editingTime.startTime;
        timeGroup.endTime = this.editingTime.endTime;
        timeGroup.timeKey = this.generateTimeKey(this.editingTime.startTime, this.editingTime.endTime);

        // Close dialog and reset
        this.dialog.closeAll();
        this.isEditingTime = false;
        this.editingTime = {};

        this.showToastMessage(`Time changes tracked for ${codesToUpdate.length} codes. Save all changes when ready.`);

        /* const date: moment.Moment = moment.isMoment(this.form.value.date) ? moment.utc(this.form.value.date.format('DD/MM/YYYY'), 'DD/MM/YYYY') : moment(this.form.value.date);

        this.form = this.fb.group({
      date: [moment(), Validators.required],
      groupId: [this.groupId, Validators.required],
      patientId: [this.patientId, Validators.required],
      procedureCodes: [[], Validators.required],
      paidCash: [false],
      noCharge: [false],
      providerId: [null], // Add providerId field
      codingBundle: [null], // Add codingBundle field
    });
        
        // Include the selected provider in the data
        const combinedData = {
            ...this.form.value,
            date: date.startOf('day').toISOString(),
            providerId: this.selectedProviderId // Link codes to the selected provider
        };


        this.billingService.saveCodeGroup(combinedData)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.showToastMessage('Patient procedure codes saved successfully');
                    this.returnToParent();
                },
                error: (error) => {
                    console.error('Error saving patient procedure codes: ', error);
                    this.showToastMessage('There was an error saving the patient procedure codes. Please try again.');
                }
            }); */

        // Update each code in the backend using RxJS forkJoin
        /* const updateObservables = codesToUpdate.map(codeUpdate =>
            this.patientBillingService.updateCode(codeUpdate.id, codeUpdate)
        );

        // Use forkJoin to wait for all updates to complete
        forkJoin(updateObservables)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    // Update local data structure
                    timeGroup.providerGroups.forEach((providerGroup: any) => {
                        providerGroup.records.forEach((record: any) => {
                            record.startTime = this.editingTime.startTime;
                            record.endTime = this.editingTime.endTime;
                        });
                    });

                    // Update the time group properties
                    timeGroup.startTime = this.editingTime.startTime;
                    timeGroup.endTime = this.editingTime.endTime;
                    timeGroup.timeKey = this.generateTimeKey(this.editingTime.startTime, this.editingTime.endTime);

                    // Refresh the data from the service to ensure consistency
                    this.loadData();

                    // Close dialog and reset
                    this.dialog.closeAll();
                    this.isEditingTime = false;
                    this.editingTime = {};

                    this.showToastMessage(`Time updated successfully for ${codesToUpdate.length} codes in this time group`);
                },
                error: (error) => {
                    console.error('Error updating times:', error);
                    this.showToastMessage('There was an error updating the times. Please try again.');
                }
            }); */
    }

    cancelTimeEdit(): void {
        this.isEditingTime = false;
        this.editingTime = {};
        this.dialog.closeAll();
    }

    saveProviderEdit(): void {
        if (!this.editingProvider.newProviderId) {
            this.showToastMessage('Please select a provider');
            return;
        }

        if (this.editingProvider.newProviderId === this.editingProvider.providerId) {
            this.showToastMessage('No change in provider selected');
            return;
        }

        // Find the patient group
        const patientGroup = this.groupedPatients.find(p => p.patient.id === this.editingProvider.patientId);
        if (!patientGroup) return;

        // Find the date group
        const dateGroup = patientGroup.dateGroups.find((dg: any) => dg.dateKey === this.editingProvider.dateKey);
        if (!dateGroup) return;

        // Find the time group
        const timeGroup = dateGroup.timeGroups.find((tg: any) => tg.timeKey === this.editingProvider.timeKey);
        if (!timeGroup) return;

        // Find the provider group
        const providerGroup = timeGroup.providerGroups.find((pg: any) => pg.providerId === this.editingProvider.providerId);
        if (!providerGroup) return;

        // Collect all the codes that need to be updated
        const codesToUpdate: any[] = [];
        providerGroup.records.forEach((record: any) => {
            if (record.id && !record.id.startsWith('temp_')) {
                codesToUpdate.push({
                    id: record.id,
                    date: record.date,
                    groupId: record.groupId,
                    code: record.code,
                    description: record.description,
                    startTime: record.startTime,
                    endTime: record.endTime,
                    weight: record.weight,
                    height: record.height,
                    BMI: record.BMI,
                    paidCash: record.paidCash,
                    noCharge: record.noCharge,
                    providerId: this.editingProvider.newProviderId,
                    displayIndex: record.displayIndex
                });
            }
        });

        if (codesToUpdate.length === 0) {
            this.showToastMessage('No codes found to update');
            return;
        }

        // Track the change instead of immediately persisting
        const changeId = this.changeTracker.generateChangeId('provider_update');
        const pendingChange: PendingChange = {
            id: changeId,
            type: 'provider-update',
            data: {
                patientId: this.editingProvider.patientId,
                dateKey: this.editingProvider.dateKey,
                timeKey: this.editingProvider.timeKey,
                oldProviderId: this.editingProvider.providerId,
                newProviderId: this.editingProvider.newProviderId,
                codesToUpdate: codesToUpdate,
                providerGroup: providerGroup
            },
            timestamp: Date.now(),
            description: `Update provider for ${codesToUpdate.length} codes from ${this.editingProvider.providerName} to new provider`
        };

        this.changeTracker.addChange(pendingChange);

        // Update local data structure immediately for responsive UI
        const newProvider = this.groupUsers.find(u => u.userId === this.editingProvider.newProviderId);
        const newProviderName = newProvider ? `${newProvider.title} ${newProvider.name} ${newProvider.surname}`.trim() : 'Unknown Provider';

        providerGroup.records.forEach((record: any) => {
            record.providerId = this.editingProvider.newProviderId;
            record.provider = newProvider;
        });

        // Update the provider group properties
        providerGroup.providerId = this.editingProvider.newProviderId;
        providerGroup.providerName = newProviderName;

        // Close dialog and reset
        this.dialog.closeAll();
        this.isEditingProvider = false;
        this.editingProvider = {};

        this.showToastMessage(`Provider changes tracked for ${codesToUpdate.length} codes. Save all changes when ready.`);
    }

    cancelProviderEdit(): void {
        this.isEditingProvider = false;
        this.editingProvider = {};
        this.dialog.closeAll();
    }

    onProviderEditSelectionChange(providerId: string): void {
        this.editingProvider.newProviderId = providerId;
    }

    saveNewCode(): void {
        if (this.selectedProcedureCodes.length === 0) {
            this.showToastMessage('Please select at least one procedure code');
            return;
        }

        /*  if (!this.creatingCode.providerId) {
             this.showToastMessage('Please select a provider');
             return;
         } */

        // Collect existing codes for this patient/date/time combination
        const existingCodes = this.collectExistingCodesForGroup();

        // Combine existing codes with new codes
        const allProcedureCodes = [
            ...existingCodes,
            ...this.selectedProcedureCodes.map(procedureCode => ({
                code: procedureCode.code,
                description: procedureCode.description,
                startTime: this.creatingCode.startTime || null,
                endTime: this.creatingCode.endTime || null,
                // Note: Don't include id for new codes
                // Note: Don't include date, providerId, paidCash, noCharge in individual codes
                // These are handled at the group level
            }))
        ];

        // Format date properly for backend - convert to UTC ISO string
        const formattedDate = this.creatingCode.date.utc().startOf('day').toISOString();

        // Prepare the data for saving - match backend expectations exactly
        const saveData = {
            date: formattedDate,
            groupId: this.groupService.group?.id,
            patientId: this.creatingCode.patientId,
            procedureCodes: allProcedureCodes,
            paidCash: this.creatingCode.paidCash,
            noCharge: this.creatingCode.noCharge,
            providerId: this.creatingCode.providerId
        };

        // Track the change instead of immediately persisting
        const changeId = this.changeTracker.generateChangeId('create_codes');
        const pendingChange: PendingChange = {
            id: changeId,
            type: 'create',
            data: saveData,
            timestamp: Date.now(),
            description: `Create ${this.selectedProcedureCodes.length} new procedure codes for patient ${this.creatingCode.patient?.name} ${this.creatingCode.patient?.surname}`
        };

        this.changeTracker.addChange(pendingChange);

        // Update local data structure immediately for responsive UI
        this.updateLocalDataForNewCodes();

        // Close dialog and reset
        this.dialog.closeAll();
        this.isCreatingCode = false;
        this.creatingCode = {};
        this.selectedProcedureCodes = [];

        this.showToastMessage(`New codes tracked for creation. Save all changes when ready.`);
    }



    cancelCreateCode(): void {
        this.isCreatingCode = false;
        this.creatingCode = {};
        this.dialog.closeAll();
    }

    /**
     * Simple method to add new codes to local data structure for immediate display
     */
    private updateLocalDataForNewCodes(): void {
        //console.log('=== updateLocalDataForNewCodes DEBUG ===');
        /* console.log('Creating code data:', {
            patientId: this.creatingCode.patientId,
            date: this.creatingCode.date,
            startTime: this.creatingCode.startTime,
            endTime: this.creatingCode.endTime,
            providerId: this.creatingCode.providerId,
            selectedCodes: this.selectedProcedureCodes.map(c => ({ code: c.code, description: c.description }))
        }); */

        // Find the target provider group based on the creation context
        const targetProviderGroup = this.findTargetProviderGroup();
        if (!targetProviderGroup) {
            console.log('Could not find target provider group for new codes');
            /* console.log('Available groupedPatients:', this.groupedPatients.map(p => ({
                patientId: p.patient.id,
                patientName: `${p.patient.name} ${p.patient.surname}`,
                dateGroups: p.dateGroups.map((dg: any) => ({
                    dateKey: dg.dateKey,
                    date: dg.date,
                    timeGroups: dg.timeGroups.map((tg: any) => ({
                        timeKey: tg.timeKey,
                        startTime: tg.startTime,
                        endTime: tg.endTime,
                        providerGroups: tg.providerGroups.map((pg: any) => ({
                            providerId: pg.providerId,
                            providerName: pg.providerName,
                            count: pg.count
                        }))
                    }))
                }))
            }))); */
            return;
        }

        // Create temporary codes with temp IDs for immediate display
        const tempCodes = this.selectedProcedureCodes.map((code, index) => ({
            id: `temp_${Date.now()}_${index}`,
            code: code.code,
            description: code.description,
            startTime: this.creatingCode.startTime,
            endTime: this.creatingCode.endTime,
            date: this.creatingCode.date,
            groupId: this.groupService.group?.id,
            patientId: this.creatingCode.patientId,
            providerId: this.creatingCode.providerId,
            provider: this.groupUsers.find(u => u.userId === this.creatingCode.providerId),
            paidCash: this.creatingCode.paidCash,
            noCharge: this.creatingCode.noCharge,
            displayIndex: targetProviderGroup.records.length + index
        }));

        // Add to the provider group - create new array reference for change detection
        targetProviderGroup.records = [...targetProviderGroup.records, ...tempCodes];
        targetProviderGroup.count += tempCodes.length;

        // Update parent group counts
        this.updateParentGroupCounts(targetProviderGroup, tempCodes.length);

        console.log('Added new codes to local data:', tempCodes.length);
    }

    /**
     * Find or create the target provider group for new codes
     */
    private findTargetProviderGroup(): any {
        console.log('=== findTargetProviderGroup DEBUG ===');
        console.log('Looking for patientId:', this.creatingCode.patientId);

        const patientGroup = this.groupedPatients.find(p => p.patient.id === this.creatingCode.patientId);
        if (!patientGroup) {
            console.log('Patient group not found for patientId:', this.creatingCode.patientId);
            console.log('Available patient IDs:', this.groupedPatients.map(p => p.patient.id));
            return null;
        }
        console.log('Found patient group:', patientGroup.patient.name, patientGroup.patient.surname);

        // Generate dateKey from the creating code date
        const dateKey = this.generateDateKey(this.creatingCode.date);
        console.log('Generated dateKey:', dateKey, 'from date:', this.creatingCode.date);
        console.log('Available dateKeys in patient group:', patientGroup.dateGroups.map((dg: any) => dg.dateKey));

        let dateGroup = patientGroup.dateGroups.find((dg: any) => dg.dateKey === dateKey);

        // Create date group if it doesn't exist
        if (!dateGroup) {
            console.log('Creating new date group for dateKey:', dateKey);
            // If we're creating a new date group, it means the date doesn't exist yet
            // So we can use the creating code date directly
            dateGroup = {
                date: this.creatingCode.date,
                dateKey: dateKey,
                timeGroups: [],
                count: 0
            };
            patientGroup.dateGroups.push(dateGroup);
            // Sort date groups
            patientGroup.dateGroups.sort((a: any, b: any) => {
                const dateA = moment(a.date);
                const dateB = moment(b.date);
                return dateB.valueOf() - dateA.valueOf();
            });
        } else {
            console.log('Found existing date group:', dateGroup.dateKey);
        }

        const timeKey = this.generateTimeKey(this.creatingCode.startTime, this.creatingCode.endTime);
        console.log('Generated timeKey:', timeKey, 'from startTime:', this.creatingCode.startTime, 'endTime:', this.creatingCode.endTime);
        console.log('Available timeKeys in date group:', dateGroup.timeGroups.map((tg: any) => tg.timeKey));

        let timeGroup = dateGroup.timeGroups.find((tg: any) => tg.timeKey === timeKey);

        // Create time group if it doesn't exist
        if (!timeGroup) {
            console.log('Creating new time group for timeKey:', timeKey);
            timeGroup = {
                startTime: this.creatingCode.startTime,
                endTime: this.creatingCode.endTime,
                timeKey: timeKey,
                providerGroups: [],
                count: 0
            };
            dateGroup.timeGroups.push(timeGroup);
            // Sort time groups
            dateGroup.timeGroups.sort((a: any, b: any) => {
                const timeA = a.startTime || '00:00';
                const timeB = b.startTime || '00:00';
                return timeB.localeCompare(timeA);
            });
        } else {
            console.log('Found existing time group:', timeGroup.timeKey);
        }

        console.log('Looking for providerId:', this.creatingCode.providerId);
        console.log('Available providerIds in time group:', timeGroup.providerGroups.map((pg: any) => pg.providerId));

        let providerGroup = timeGroup.providerGroups.find((pg: any) => pg.providerId === this.creatingCode.providerId);

        // Create provider group if it doesn't exist
        if (!providerGroup) {
            console.log('Creating new provider group for providerId:', this.creatingCode.providerId);
            const provider = this.groupUsers.find(u => u.userId === this.creatingCode.providerId);
            const providerName = provider ? `${provider.title} ${provider.name} ${provider.surname}`.trim() : 'Unknown Provider';
            console.log('Provider details:', { provider, providerName });

            providerGroup = {
                providerId: this.creatingCode.providerId,
                providerName: providerName,
                records: [],
                count: 0,
                providerGroupKey: `${patientGroup.patient.id}-${dateGroup.dateKey}-${timeGroup.timeKey}-${this.creatingCode.providerId}`
            };
            timeGroup.providerGroups.push(providerGroup);
        } else {
            console.log('Found existing provider group:', providerGroup.providerName);
        }

        console.log('Final provider group:', {
            providerId: providerGroup.providerId,
            providerName: providerGroup.providerName,
            providerGroupKey: providerGroup.providerGroupKey,
            currentRecordCount: providerGroup.records.length
        });

        return providerGroup;
    }

    /**
     * Update parent group counts when adding new codes
     */
    private updateParentGroupCounts(providerGroup: any, addedCount: number): void {
        // Find the time group that contains this provider group
        for (const patientGroup of this.groupedPatients) {
            for (const dateGroup of patientGroup.dateGroups) {
                for (const timeGroup of dateGroup.timeGroups) {
                    if (timeGroup.providerGroups.includes(providerGroup)) {
                        timeGroup.count += addedCount;
                        dateGroup.count += addedCount;
                        patientGroup.totalRecords += addedCount;
                        return;
                    }
                }
            }
        }
    }

    cancelEditCode(): void {
        this.editingCode = {};
        this.selectedProviderId = null;
        this.dialog.closeAll();
    }

    onProcedureCodesChanged(codes: ProcedureCode[]): void {
        this.selectedProcedureCodes = codes;
    }

    /**
     * Simplified method for handling procedure code changes (drag & drop, etc.)
     * Tracks pending changes instead of autosaving
     */
    onExistingProcedureCodesChanged(codes: ProcedureCode[], providerGroupKey: string): void {
        console.log('Procedure codes changed for provider group:', providerGroupKey, {
            codesCount: codes.length,
            codes: codes.map(c => ({ code: c.code, displayIndex: c.displayIndex, id: c.id }))
        });

        // Find the provider group and update the records
        const context = this.findProviderGroupContext(providerGroupKey);
        if (!context) {
            console.error('Could not find context for provider group:', providerGroupKey);
            return;
        }

        // Store original records before updating for change detection
        const originalRecords = [...context.providerGroup.records];

        // Update local records immediately for responsive UI
        context.providerGroup.records = codes;
        context.providerGroup.count = codes.length;
        context.timeGroup.count = this.calculateTimeGroupCount(context.timeGroup);
        context.dateGroup.count = this.calculateDateGroupCount(context.dateGroup);
        context.patientGroup.totalRecords = this.calculatePatientTotalCount(context.patientGroup);

        // Track order changes in the main change tracker
        if (codes.length > 0 && codes[0].id && !codes[0].id.startsWith('temp_')) {
            // Check if there are actual order changes by comparing with original records
            const hasOrderChanges = codes.length !== originalRecords.length ||
                codes.some((code, index) => {
                    const originalRecord = originalRecords[index];
                    return !originalRecord || originalRecord.id !== code.id;
                });

            console.log('Order change detection:', {
                providerGroupKey,
                hasOrderChanges,
                codesCount: codes.length,
                originalCount: originalRecords.length,
                codes: codes.map(c => ({ id: c.id, displayIndex: c.displayIndex, code: c.code })),
                original: originalRecords.map(r => ({ id: r.id, displayIndex: r.displayIndex, code: r.code }))
            });

            if (hasOrderChanges) {
                // Remove any existing order change for this provider group
                this.removeExistingOrderChange(providerGroupKey);

                // Collect all existing codes for this patient/date combination
                const allExistingCodes = this.collectAllExistingCodesForPatientDate(context.patientGroup.patient.id, context.dateGroup.date);

                // Update the displayIndex values for the codes in the current provider group
                const updatedCodes = allExistingCodes.map(existingCode => {
                    const updatedCode = codes.find(c => c.code === existingCode.code && c.id === existingCode.id);
                    if (updatedCode) {
                        return {
                            ...existingCode,
                            displayIndex: updatedCode.displayIndex ?? existingCode.displayIndex
                        };
                    }
                    return existingCode;
                });

                // Prepare the data for saving
                const saveData = {
                    date: context.dateGroup.date,
                    groupId: this.groupService.group?.id,
                    patientId: context.patientGroup.patient.id,
                    procedureCodes: updatedCodes,
                    paidCash: undefined, // Preserve existing values
                    noCharge: undefined, // Preserve existing values
                    providerId: undefined // Preserve existing providerId values
                };

                // Track the change in the main change tracker
                const changeId = this.changeTracker.generateChangeId('order_update');
                const pendingChange: PendingChange = {
                    id: changeId,
                    type: 'order-update',
                    data: saveData,
                    timestamp: Date.now(),
                    description: `Update order for ${codes.length} procedure codes in provider group ${providerGroupKey}`
                };

                this.changeTracker.addChange(pendingChange);

                console.log('Order change tracked:', {
                    changeId: pendingChange.id,
                    totalChanges: this.changeTracker.getAllChanges().length,
                    hasPendingChanges: this.changeTracker.hasChanges()
                });
            }
        }
    }

    /**
     * Simplified method to find provider group context
     */
    private findProviderGroupContext(providerGroupKey: string): any {
        for (const patientGroup of this.groupedPatients) {
            for (const dateGroup of patientGroup.dateGroups) {
                for (const timeGroup of dateGroup.timeGroups) {
                    const providerGroup = timeGroup.providerGroups.find((pg: any) => pg.providerGroupKey === providerGroupKey);
                    if (providerGroup) {
                        return {
                            patientGroup,
                            dateGroup,
                            timeGroup,
                            providerGroup
                        };
                    }
                }
            }
        }
        return null;
    }

    /**
     * Calculate counts for nested groups
     */
    private calculateTimeGroupCount(timeGroup: any): number {
        return timeGroup.providerGroups.reduce((total: number, pg: any) => total + pg.count, 0);
    }

    /**
     * Remove existing order change for a provider group to avoid duplicates
     */
    private removeExistingOrderChange(providerGroupKey: string): void {
        const allChanges = this.changeTracker.getAllChanges();
        const orderChanges = allChanges.filter(change =>
            change.type === 'order-update' &&
            change.description.includes(providerGroupKey)
        );

        orderChanges.forEach(change => {
            this.changeTracker.removeChange(change.id);
        });
    }

    private calculateDateGroupCount(dateGroup: any): number {
        return dateGroup.timeGroups.reduce((total: number, tg: any) => total + tg.count, 0);
    }

    private calculatePatientTotalCount(patientGroup: any): number {
        return patientGroup.dateGroups.reduce((total: number, dg: any) => total + dg.count, 0);
    }



    /**
     * Simplified deletion handler - using the same approach as patient-coding-detail
     */
    onProcedureCodeDeleted(code: ProcedureCode, providerGroupKey?: string): void {
        console.log('onProcedureCodeDeleted called with:', { code, providerGroupKey });

        if (providerGroupKey) {
            // Simple local update - just remove the code from the data structure
            const context = this.findProviderGroupContext(providerGroupKey);
            if (context) {
                const codeIndex = context.providerGroup.records.findIndex((r: any) =>
                    r.code === code.code && r.id === code.id
                );

                if (codeIndex >= 0) {
                    context.providerGroup.records.splice(codeIndex, 1);
                    context.providerGroup.count--;
                    context.timeGroup.count = this.calculateTimeGroupCount(context.timeGroup);
                    context.dateGroup.count = this.calculateDateGroupCount(context.dateGroup);
                    context.patientGroup.totalRecords = this.calculatePatientTotalCount(context.patientGroup);
                }
            }

            // Track the deletion instead of immediately persisting
            if (code.id && !code.id.startsWith('temp_')) {
                const changeId = this.changeTracker.generateChangeId('delete_code');
                const pendingChange: PendingChange = {
                    id: changeId,
                    type: 'delete',
                    data: {
                        codeId: code.id,
                        code: code.code,
                        description: code.description
                    },
                    timestamp: Date.now(),
                    description: `Delete procedure code ${code.code} - ${code.description}`
                };

                this.changeTracker.addChange(pendingChange);
                this.showToastMessage(`Code deletion tracked. Save all changes when ready.`);
            }
        }
        // When !providerGroupKey, selectedProcedureCodes is already updated by onProcedureCodesChanged.
    }

    private collectAllExistingCodesForPatientDate(patientId: string, date: string): any[] {
        const existingCodes: any[] = [];

        // Find the patient group
        const patientGroup = this.groupedPatients.find(p => p.patient.id === patientId);
        if (!patientGroup) return existingCodes;

        // Find the date group
        const dateKey = this.generateDateKey(date);
        const dateGroup = patientGroup.dateGroups.find((dg: any) => dg.dateKey === dateKey);
        if (!dateGroup) return existingCodes;

        // Collect ALL existing codes from this date, regardless of time or provider
        dateGroup.timeGroups.forEach((timeGroup: any) => {
            timeGroup.providerGroups.forEach((providerGroup: any) => {
                providerGroup.records.forEach((record: any) => {
                    if (record.id && !record.id.startsWith('temp_')) {
                        existingCodes.push({
                            id: record.id,
                            code: record.code,
                            description: record.description,
                            startTime: record.startTime,
                            endTime: record.endTime,
                            weight: record.weight,
                            height: record.height,
                            BMI: record.BMI,
                            paidCash: record.paidCash,
                            noCharge: record.noCharge,
                            displayIndex: record.displayIndex
                        });
                    }
                });
            });
        });

        return existingCodes;
    }

    saveCodeEdit(): void {
        if (!this.editingCode.id) {
            console.error('Cannot save code without ID');
            return;
        }

        // Prepare update data
        const updateData = {
            date: this.editingCode.date,
            startTime: this.editingCode.startTime,
            endTime: this.editingCode.endTime,
            weight: this.editingCode.weight,
            height: this.editingCode.height,
            BMI: this.editingCode.BMI,
            paidCash: this.editingCode.paidCash,
            noCharge: this.editingCode.noCharge,
            providerId: this.selectedProviderId,
            displayIndex: this.editingCode.displayIndex
        };

        // Track the change instead of immediately persisting
        const changeId = this.changeTracker.generateChangeId('update_code');
        const pendingChange: PendingChange = {
            id: changeId,
            type: 'update',
            data: {
                codeId: this.editingCode.id,
                updateData: updateData
            },
            timestamp: Date.now(),
            description: `Update procedure code ${this.editingCode.code} - ${this.editingCode.description}`
        };

        this.changeTracker.addChange(pendingChange);

        // Close dialog and reset
        this.dialog.closeAll();
        this.editingCode = {};
        this.selectedProviderId = null;

        this.showToastMessage(`Code update tracked. Save all changes when ready.`);
    }

    onEditDialogProviderChange(providerId: string): void {
        this.selectedProviderId = providerId;
        // Also update the editingCode for consistency
        if (this.editingCode.id) {
            this.editingCode.providerId = providerId;
        }
    }

    onProviderSelectionChange(providerId: string): void {
        if (this.editingCode.id) {
            // Editing existing code
            this.onEditDialogProviderChange(providerId);
        } else {
            // Creating new code
            this.creatingCode.providerId = providerId;
        }
    }

    // Helper methods for template bindings
    getCurrentDate(): any {
        return this.editingCode.id ? this.editingCode.date : this.creatingCode.date;
    }

    getCurrentStartTime(): any {
        return this.editingCode.id ? this.editingCode.startTime : this.creatingCode.startTime;
    }

    getCurrentEndTime(): any {
        return this.editingCode.id ? this.editingCode.endTime : this.creatingCode.endTime;
    }

    getCurrentProviderId(): any {
        return this.editingCode.id ? this.selectedProviderId : this.creatingCode.providerId;
    }

    getCurrentPaidCash(): any {
        return this.editingCode.id ? this.editingCode.paidCash : this.creatingCode.paidCash;
    }

    getCurrentNoCharge(): any {
        return this.editingCode.id ? this.editingCode.noCharge : this.creatingCode.noCharge;
    }

    onDateChange(value: any): void {
        if (this.editingCode.id) {
            this.editingCode.date = value;
        } else {
            this.creatingCode.date = value;
        }
    }

    onStartTimeChange(value: any): void {
        if (this.editingCode.id) {
            this.editingCode.startTime = value;
        } else {
            this.creatingCode.startTime = value;
        }
    }

    onEndTimeChange(value: any): void {
        if (this.editingCode.id) {
            this.editingCode.endTime = value;
        } else {
            this.creatingCode.endTime = value;
        }
    }

    onPaidCashChange(value: any): void {
        if (this.editingCode.id) {
            this.editingCode.paidCash = value;
        } else {
            this.creatingCode.paidCash = value;
        }
    }

    onNoChargeChange(value: any): void {
        if (this.editingCode.id) {
            this.editingCode.noCharge = value;
        } else {
            this.creatingCode.noCharge = value;
        }
    }

    openEditDialog(code: ProcedureCode): void {
        // Find the actual record in our data structure
        const record = this.findRecordByCode(code);
        if (!record) {
            console.error('Record not found for code:', code);
            return;
        }

        // Initialize editing state
        this.editingCode = { ...record };
        this.selectedProviderId = record.providerId;

        // Open the edit dialog
        this.dialog.open(this.addCodeDialog!);
    }

    private findRecordByCode(code: ProcedureCode): any {
        // Search through all records to find the one matching this code
        for (const patientGroup of this.groupedPatients) {
            for (const dateGroup of patientGroup.dateGroups) {
                for (const timeGroup of dateGroup.timeGroups) {
                    for (const providerGroup of timeGroup.providerGroups) {
                        const record = providerGroup.records.find((r: any) => r.code === code.code);
                        if (record) {
                            return record;
                        }
                    }
                }
            }
        }
        return null;
    }

    navigateToPatientFile(patientId: string) {
        this.patientService.getPatientById(patientId)
            .pipe(
                takeUntil(this.destroy$)
            )
            .subscribe(result => {
                this.router.navigate(['/patients', {
                    outlets: {
                        patient: ['file', patientId],
                    }
                }]);
            }, error => {
                console.error(error);
                this.showToastMessage('There was an error loading the patient file. Please try again.');
            });
    }

    onDataChange(): void {
        this.dataChange.emit(this.clonedData);
    }

    private collectExistingCodesForGroup(): any[] {
        const existingCodes: any[] = [];

        // Find the patient group
        const patientGroup = this.groupedPatients.find(p => p.patient.id === this.creatingCode.patientId);
        if (!patientGroup) return existingCodes;

        // Find the date group - handle moment objects properly
        const dateForKey = moment.isMoment(this.creatingCode.date) ? this.creatingCode.date.toISOString() : this.creatingCode.date;
        const dateKey = this.generateDateKey(dateForKey);
        const dateGroup = patientGroup.dateGroups.find((dg: any) => dg.dateKey === dateKey);
        if (!dateGroup) return existingCodes;

        // Always collect ALL existing codes from this date, regardless of time
        // This ensures we preserve all existing codes when adding new ones
        dateGroup.timeGroups.forEach((timeGroup: any) => {
            timeGroup.providerGroups.forEach((providerGroup: any) => {
                providerGroup.records.forEach((record: any) => {
                    if (record.id && !record.id.startsWith('temp_')) {
                        existingCodes.push({
                            id: record.id, // Include ID for existing codes
                            code: record.code,
                            description: record.description,
                            startTime: record.startTime,
                            endTime: record.endTime,
                            weight: record.weight,
                            height: record.height,
                            BMI: record.BMI,
                            paidCash: record.paidCash,
                            noCharge: record.noCharge
                        });
                    }
                });
            });
        });

        return existingCodes;
    }



    /**
     * Get the count of pending changes
     */
    get pendingChangesCount(): number {
        return this.changeTracker.getAllChanges().length;
    }

    /**
     * Debug method to test change tracking
     */
    testChangeTracking(): void {
        const testChange: PendingChange = {
            id: this.changeTracker.generateChangeId('test'),
            type: 'order-update',
            data: { test: true },
            timestamp: Date.now(),
            description: 'Test change for debugging'
        };

        this.changeTracker.addChange(testChange);
        console.log('Test change added:', {
            totalChanges: this.changeTracker.getAllChanges().length,
            hasPendingChanges: this.changeTracker.hasChanges()
        });
    }


    /**
     * Save all pending changes to the backend
     * 
     * Note: Backend behavior:
     * - saveCodeGroup: Updates existing codes but does NOT update providerId (line 148-149 commented out)
     * - updateCodeDate: Updates individual codes including providerId
     * - For provider changes, we must use updateCodeDate instead of saveCodeGroup
     */
    saveAllPendingChanges(): void {
        if (!this.changeTracker.hasChanges()) {
            this.showToastMessage('No pending changes to save');
            return;
        }

        if (this.isPersistingData) {
            this.showToastMessage('Save operation already in progress');
            return;
        }

        this.isPersistingData = true;
        const allChanges = this.changeTracker.getAllChanges();

        console.log('Saving all pending changes:', allChanges);

        // Group changes by type for efficient processing
        const createChanges = this.changeTracker.getChangesByType('create');
        const updateChanges = this.changeTracker.getChangesByType('update');
        const deleteChanges = this.changeTracker.getChangesByType('delete');
        const timeUpdateChanges = this.changeTracker.getChangesByType('time-update');
        const orderUpdateChanges = this.changeTracker.getChangesByType('order-update');
        const providerUpdateChanges = this.changeTracker.getChangesByType('provider-update');

        const operations: Promise<any>[] = [];

        // Process creates
        createChanges.forEach(change => {
            operations.push(
                this.patientBillingService.saveCodeGroup(change.data).toPromise()
            );
        });

        // Process updates - filter out temp codes
        updateChanges.forEach(change => {
            // Skip updates for temporary codes (they should be handled by create operations)
            if (change.data.codeId && change.data.codeId.startsWith('temp_')) {
                console.log(`Skipping update for temporary code: ${change.data.codeId}`);
                return;
            }

            operations.push(
                this.patientBillingService.updateCode(change.data.codeId, change.data.updateData).toPromise()
            );
        });

        // Process deletes - filter out temp codes
        deleteChanges.forEach(change => {
            // Skip deletes for temporary codes (they don't exist in the database)
            if (change.data.codeId && change.data.codeId.startsWith('temp_')) {
                console.log(`Skipping delete for temporary code: ${change.data.codeId}`);
                return;
            }

            operations.push(
                this.patientBillingService.deleteCode(change.data.codeId).toPromise()
                    .then(response => {
                        // Handle the case where the code was already deleted
                        if (response.alreadyDeleted) {
                            console.log(`Code ${change.data.codeId} was already deleted - treating as success`);
                            return { success: true, alreadyDeleted: true };
                        }
                        return response;
                    })
                    .catch(error => {
                        // If it's a "not found" error, treat it as already deleted
                        if (error.message && error.message.includes('not found')) {
                            console.log(`Code ${change.data.codeId} not found - treating as already deleted`);
                            return { success: true, alreadyDeleted: true };
                        }
                        throw error;
                    })
            );
        });

        // Process time updates
        timeUpdateChanges.forEach(change => {
            const { codesToUpdate, patientId, dateKey } = change.data;
            const extractedDate = codesToUpdate[0].date;
            const extractedGroupId = codesToUpdate[0].groupId;
            const date = moment.isMoment(extractedDate) ? moment.utc(extractedDate.format('DD/MM/YYYY'), 'DD/MM/YYYY') : moment(extractedDate);

            const combinedData = {
                date: date.startOf('day').toISOString(),
                groupId: extractedGroupId || this.groupService.group?.id,
                patientId: patientId,
                procedureCodes: codesToUpdate,
                // Preserve existing values for these fields
                paidCash: undefined,
                noCharge: undefined,
                providerId: undefined
            };

            operations.push(
                this.patientBillingService.saveCodeGroup(combinedData).toPromise()
            );
        });

        // Process order updates
        orderUpdateChanges.forEach(change => {
            operations.push(
                this.patientBillingService.saveCodeGroup(change.data).toPromise()
            );
        });

        // Process provider updates
        // Note: The backend saveCodeGroup doesn't update providerId for existing codes
        // So we need to use individual updateCode calls for provider changes
        providerUpdateChanges.forEach(change => {
            const { codesToUpdate } = change.data;
            codesToUpdate.forEach((codeUpdate: any) => {
                // Skip updates for temporary codes (they should be handled by create operations)
                if (codeUpdate.id && codeUpdate.id.startsWith('temp_')) {
                    console.log(`Skipping provider update for temporary code: ${codeUpdate.id}`);
                    return;
                }

                operations.push(
                    this.patientBillingService.updateCode(codeUpdate.id, {
                        providerId: codeUpdate.providerId,
                        date: codeUpdate.date,
                        startTime: codeUpdate.startTime,
                        endTime: codeUpdate.endTime,
                        weight: codeUpdate.weight,
                        height: codeUpdate.height,
                        BMI: codeUpdate.BMI,
                        paidCash: codeUpdate.paidCash,
                        noCharge: codeUpdate.noCharge,
                        displayIndex: codeUpdate.displayIndex
                    }).toPromise()
                );
            });
        });

        // Execute all operations
        Promise.all(operations)
            .then(() => {
                this.showToastMessage(`All changes saved successfully (${allChanges.length} operations)`);
                this.changeTracker.clearChanges();
                this.isPersistingData = false;

                // Refresh data from service to ensure consistency
                this.loadData();
            })
            .catch((error) => {
                console.error('Error saving changes:', error);
                this.showToastMessage('There was an error saving some changes. Please try again.');
                this.isPersistingData = false;

                // Reload data to restore original state
                this.loadData();
            });
    }

    /**
     * Discard all pending changes
     */
    discardAllPendingChanges(): void {
        if (!this.changeTracker.hasChanges()) {
            this.showToastMessage('No pending changes to discard');
            return;
        }

        const changeCount = this.changeTracker.getAllChanges().length;
        this.changeTracker.clearChanges();

        // Reload data to restore original state
        this.loadData();

        this.showToastMessage(`Discarded ${changeCount} pending changes`);
    }
}
